import type { CertificationPreview } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import type { GetStaticPaths, GetStaticProps } from "next";
import { useRouter } from "next/compat/router";
import { NextSeo } from "next-seo";
import { useEffect } from "react";
import { mutate } from "swr";

import CertificationOutOfScopeOverlay from "./components/CertificationOutOfScopeOverlay";
import CertificationRecommendedCoursePlan from "./components/CertificationRecommendedCoursePlan";
import CertificationSidebar from "./components/CertificationSidebar";
import CertificationTutorial from "./components/CertificationTutorial";
import { useCertification } from "~/hooks";
import { GridLayout } from "~/layouts";
import type { NextPageWithProps } from "~/types";
import fetcher from "~/utils/fetcher.client";

const CertificationPage: NextPageWithProps = () => {
	const { certification } = useCertification();

	const router = useRouter();
	const { userId } = useAuth();

	useEffect(() => {
		if (certification.isNotAllowed && certification.isLegacy) {
			router?.replace(router?.asPath.replace("-original", ""));
		}
	}, [certification.isLegacy, certification.isNotAllowed, router]);

	useEffect(() => {
		if (userId) {
			mutate(`/certifications`);
			mutate(`/certifications/${router?.query.certificationSlug}`);
		}
	}, [router?.query.certificationSlug, router?.query.moduleSlug, userId]);

	return (
		<>
			<NextSeo
				title={`Recommended Course Plan - ${certification.title}`}
				description={certification.description}
				openGraph={{
					images: [
						{
							url: certification.thumbnail.url,
							alt: certification.thumbnail.alt,
						},
					],
				}}
			/>

			<CertificationTutorial />

			<CertificationOutOfScopeOverlay />

			<GridLayout.Sidebar className="hidden xl:block">
				<CertificationSidebar />
			</GridLayout.Sidebar>

			<GridLayout.Body>
				<CertificationRecommendedCoursePlan />
			</GridLayout.Body>
		</>
	);
};

CertificationPage.Layout = GridLayout;

export const getStaticPaths: GetStaticPaths = async () => {
	const certifications = await fetcher("/certifications");

	return {
		paths: certifications
			.filter((c: CertificationPreview) => c.slug !== "courses")
			.map(
				(c: CertificationPreview) =>
					`/certifications/${c.slug}/recommended-course-plan`,
			),
		fallback: "blocking",
	};
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
	try {
		const [certification, certifications, recommendedCoursePlan] =
			await Promise.all([
				fetcher(`/certifications/${params!.certificationSlug}`),
				fetcher(`/certifications`),
				fetcher(
					`/certifications/${params!.certificationSlug}/recommended-course-plan`,
				),
			]);

		const breadcrumbs = [
			{
				name: certification.title,
				href: `/certifications/${certification.slug}`,
			},
			{
				name: "Recommended Course Plan",
				href: `/certifications/${certification.slug}/recommended-course-plan`,
			},
		];

		return {
			props: {
				breadcrumbs,
				fallback: {
					[`/certifications/${params!.certificationSlug}`]: certification,
					[`/certifications`]: certifications,
					[`/certifications/${
						params!.certificationSlug
					}/recommended-course-plan`]: recommendedCoursePlan,
				},
			},
		};
	} catch (_error) {
		return {
			notFound: true,
		};
	}
};

export default CertificationPage;
